import {
  AddOrganizationRequest,
  AddOrganizationResponse,
  EditOrganizationRequest,
  GetMyAccessibleCompaniesResponse,
  GetMyOrganizationsResponse,
  GetOrganizationHasCompaniesResponse,
  GetOrganizationsResponse,
  OrganizationLabel,
  OrganizationType,
} from '@upright/common/iam/organizations/organization-types';
import { keyBy } from 'lodash';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  addOrganization,
  editOrganization,
  getAllOrganizations,
  getMyAccessibleCompanies,
  getMyOrganizations,
  getOrganization,
  getOrganizationCountsByLabel,
  getOrganizationCountsByType,
  getOrganizationHasCompanies,
} from '.';
import { useGetLoggedInUser } from '../../../common/generic/hooks';

export function useGetOrganization(id: number | undefined) {
  return useQuery({
    queryKey: ['getSingleOrganization', id],
    queryFn: async () => {
      if (id === undefined) {
        throw Error('Unexpected.');
      }
      return await getOrganization(id);
    },
    enabled: id !== undefined,
  });
}

export function useEditOrganizationMutation(id: number) {
  const queryCache = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EditOrganizationRequest) => await editOrganization(payload, id),

    onSettled: async () => {
      // A label change moves the organization between the list's label filters and their counts.
      await Promise.all([
        queryCache.invalidateQueries({ queryKey: ['getSingleOrganization', id] }),
        queryCache.invalidateQueries({ queryKey: ['getAllOrganizations'] }),
        queryCache.invalidateQueries({ queryKey: ['getOrganizationCountsByLabel'] }),
      ]);
    },
  });
}

export function useMyOrganizationsQuery(
  options?: Omit<UseQueryOptions<GetMyOrganizationsResponse, Error>, 'queryKey' | 'queryFn'>,
) {
  const user = useGetLoggedInUser();
  return useQuery({
    queryKey: ['myOrganizations', user?.id],
    queryFn: getMyOrganizations,
    gcTime: Infinity,
    ...options,
  });
}

export function useMyAccessibleCompaniesQuery(
  options?: Omit<UseQueryOptions<GetMyAccessibleCompaniesResponse, Error>, 'queryKey' | 'queryFn'>,
) {
  const user = useGetLoggedInUser();
  return useQuery({
    queryKey: ['myAccessibleCompanies', user?.id],
    queryFn: getMyAccessibleCompanies,
    gcTime: Infinity,
    ...options,
  });
}

/**
 * Lightweight check for whether the organization has any CUSTOMER_SCENARIO companies.
 * Only requires org membership (no admin privileges), so safe to call from non-admin members.
 * Used by the DMA Consultant dashboard to distinguish between "org has no assessments"
 * (show "Create free trial") and "org has assessments but I don't have access to any"
 * (show "No access" card).
 */
export function useOrgHasCompaniesQuery(
  organizationId: number | undefined,
  options?: Omit<UseQueryOptions<GetOrganizationHasCompaniesResponse, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery({
    queryKey: ['orgHasCompanies', organizationId],
    queryFn: async () => await getOrganizationHasCompanies(organizationId!),
    enabled: organizationId !== undefined,
    ...options,
  });
}

export function usePreFetchMyOrganizations() {
  const queryClient = useQueryClient();
  const user = useGetLoggedInUser();
  useEffect(() => {
    void (async () => {
      if (user) {
        await queryClient.prefetchQuery({
          queryKey: ['myOrganizations', user.id],
          queryFn: getMyOrganizations,
          gcTime: Infinity,
        });
      }
    })();
    // We only want to run this query once when the user logs in, hence no need to have queryClient in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
}

export function useGetAllOrganizations<T = GetOrganizationsResponse>(
  select?: (data: GetOrganizationsResponse) => T,
  organizationType?: OrganizationType,
  name?: string,
  options?: { enabled?: boolean; label?: OrganizationLabel },
) {
  return useQuery<GetOrganizationsResponse, Error, T>({
    queryKey: ['getAllOrganizations', organizationType, name ?? null, options?.label ?? null],

    queryFn: async () => {
      return await getAllOrganizations(organizationType, name, options?.label);
    },

    enabled: options?.enabled ?? true,
    select,
  });
}

export function useGetOrganizationCountsByType() {
  return useQuery({
    queryKey: ['getOrganizationCountsByType'],
    queryFn: getOrganizationCountsByType,
  });
}

export function useGetOrganizationCountsByLabel() {
  return useQuery({
    queryKey: ['getOrganizationCountsByLabel'],
    queryFn: getOrganizationCountsByLabel,
  });
}

export function useAllOrganizationsById() {
  return useGetAllOrganizations(organizations => keyBy(organizations, o => o.id));
}

export function useAddOrganization() {
  return useMutation<AddOrganizationResponse, Error, AddOrganizationRequest>({
    mutationKey: ['addOrganization'],
    mutationFn: async (payload: AddOrganizationRequest) => {
      return await addOrganization(payload);
    },
  });
}
